import {
  AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges,
  OnDestroy, Output, SimpleChanges, ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  CropTransform, CROP_MAX_ZOOM, clampTransform, computeSourceRect, defaultCropTransform,
} from './image-crop.util';

/**
 * Editor de recorte estilo Instagram para UMA imagem do post.
 * - Quadro na proporção `aspect` (compartilhada pelo post); imagem preenche (cover).
 * - Arrastar reposiciona; scroll/pinça dá zoom (>= 1, nunca deixa borda).
 * - Emite a transformação (scale, cx, cy) por `transformChange` (two-way `[(transform)]`).
 * O recorte final (canvas) é feito no submit pelo `cropImageToFile` do util.
 */
@Component({
  selector: 'app-post-image-cropper',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="pic-frame" #frame
         [style.aspect-ratio]="aspect"
         (pointerdown)="onPointerDown($event)"
         (pointermove)="onPointerMove($event)"
         (pointerup)="onPointerUp($event)"
         (pointercancel)="onPointerUp($event)"
         (pointerleave)="onPointerUp($event)"
         (wheel)="onWheel($event)"
         (touchstart)="$event.stopPropagation()"
         (touchmove)="$event.stopPropagation()"
         (touchend)="$event.stopPropagation()">
      <img *ngIf="src" [src]="src" alt="" draggable="false"
           class="pic-img" [style.width.px]="disp.w" [style.height.px]="disp.h"
           [style.transform]="'translate(' + disp.x + 'px,' + disp.y + 'px)'"
           (load)="onImgLoad($event)" />
      <div class="pic-zoom-wrap" *ngIf="src" (pointerdown)="$event.stopPropagation()">
        <i class="pi pi-search-minus"></i>
        <input class="pic-zoom" type="range" min="1" [max]="maxZoom" step="0.01"
               [value]="t.scale" (input)="onZoomSlider($any($event.target).value)"
               aria-label="zoom" />
        <i class="pi pi-search-plus"></i>
      </div>
    </div>
  `,
  styles: [`
    :host { display:block; width:100%; height:100%; }
    .pic-frame {
      position:relative; width:100%; height:100%; overflow:hidden; background:#000;
      touch-action:none; cursor:grab; user-select:none;
    }
    .pic-frame:active { cursor:grabbing; }
    .pic-img { position:absolute; left:0; top:0; max-width:none; will-change:transform; }
    .pic-zoom-wrap {
      position:absolute; left:50%; bottom:12px; transform:translateX(-50%);
      display:flex; align-items:center; gap:8px; padding:6px 12px;
      background:rgba(0,0,0,.55); border-radius:20px; color:#fff; z-index:3;
      opacity:0; transition:opacity .15s; pointer-events:auto;
    }
    .pic-frame:hover .pic-zoom-wrap, .pic-zoom-wrap:focus-within { opacity:1; }
    .pic-zoom-wrap i { font-size:12px; }
    .pic-zoom { width:130px; accent-color:#e00000; cursor:pointer; }
  `],
})
export class PostImageCropperComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() src: string | null = null;
  @Input() aspect = 1;
  @Input() transform: CropTransform = defaultCropTransform();
  @Output() transformChange = new EventEmitter<CropTransform>();

  @ViewChild('frame', { static: false }) frameRef?: ElementRef<HTMLElement>;

  readonly maxZoom = CROP_MAX_ZOOM;
  t: CropTransform = defaultCropTransform();
  disp = { w: 0, h: 0, x: 0, y: 0 };

  private naturalW = 0;
  private naturalH = 0;
  private frameW = 0;
  private frameH = 0;
  private ro?: ResizeObserver;

  // Estado de gesto
  private pointers = new Map<number, { x: number; y: number }>();
  private lastPan?: { x: number; y: number };
  private pinchStartDist = 0;
  private pinchStartScale = 1;

  ngOnChanges(ch: SimpleChanges): void {
    if (ch['transform'] && this.transform) this.t = { ...this.transform };
    if (ch['aspect'] || ch['transform']) {
      this.reclampAndRender();
    }
  }

  ngAfterViewInit(): void {
    this.measureFrame();
    if (typeof ResizeObserver !== 'undefined' && this.frameRef) {
      this.ro = new ResizeObserver(() => { this.measureFrame(); this.render(); });
      this.ro.observe(this.frameRef.nativeElement);
    }
  }

  ngOnDestroy(): void { this.ro?.disconnect(); }

  onImgLoad(ev: Event): void {
    const img = ev.target as HTMLImageElement;
    this.naturalW = img.naturalWidth;
    this.naturalH = img.naturalHeight;
    this.measureFrame();
    this.reclampAndRender();
  }

  private measureFrame(): void {
    const el = this.frameRef?.nativeElement;
    if (!el) return;
    this.frameW = el.clientWidth;
    this.frameH = el.clientHeight;
  }

  /** Reaplica limites e recalcula a exibição. Só emite quando `emit` = true
   *  (gesto do usuário) — nunca durante ngOnChanges, evitando loop de change-detection. */
  private reclampAndRender(emit = false): void {
    if (this.naturalW && this.naturalH) {
      this.t = clampTransform(this.naturalW, this.naturalH, this.aspect, this.t);
      if (emit) this.transformChange.emit({ ...this.t });
    }
    this.render();
  }

  /** Recalcula width/height/translate da imagem a partir do retângulo-fonte visível. */
  private render(): void {
    if (!this.naturalW || !this.naturalH || !this.frameW || !this.frameH) return;
    const { sx, sy, sw } = computeSourceRect(this.naturalW, this.naturalH, this.aspect, this.t);
    const k = this.frameW / sw; // px de tela por px natural
    this.disp = {
      w: this.naturalW * k,
      h: this.naturalH * k,
      x: -sx * k,
      y: -sy * k,
    };
  }

  // ---------- Gestos ----------
  onPointerDown(ev: PointerEvent): void {
    (ev.target as HTMLElement).setPointerCapture?.(ev.pointerId);
    this.pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
    if (this.pointers.size === 1) {
      this.lastPan = { x: ev.clientX, y: ev.clientY };
    } else if (this.pointers.size === 2) {
      this.pinchStartDist = this.currentPinchDist();
      this.pinchStartScale = this.t.scale;
      this.lastPan = undefined;
    }
  }

  onPointerMove(ev: PointerEvent): void {
    if (!this.pointers.has(ev.pointerId)) return;
    this.pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });

    if (this.pointers.size >= 2) {
      const dist = this.currentPinchDist();
      if (this.pinchStartDist > 0) {
        const scale = this.pinchStartScale * (dist / this.pinchStartDist);
        this.applyZoom(scale);
      }
      return;
    }
    if (this.lastPan && this.naturalW) {
      const dx = ev.clientX - this.lastPan.x;
      const dy = ev.clientY - this.lastPan.y;
      this.lastPan = { x: ev.clientX, y: ev.clientY };
      this.applyPan(dx, dy);
    }
  }

  onPointerUp(ev: PointerEvent): void {
    this.pointers.delete(ev.pointerId);
    if (this.pointers.size < 2) this.pinchStartDist = 0;
    if (this.pointers.size === 1) {
      const only = this.pointers.values().next().value;
      this.lastPan = only ? { x: only.x, y: only.y } : undefined;
    } else if (this.pointers.size === 0) {
      this.lastPan = undefined;
    }
  }

  onWheel(ev: WheelEvent): void {
    ev.preventDefault();
    const factor = ev.deltaY < 0 ? 1.08 : 1 / 1.08;
    this.applyZoom(this.t.scale * factor);
  }

  onZoomSlider(value: string | number): void {
    this.applyZoom(Number(value));
  }

  private currentPinchDist(): number {
    const pts = Array.from(this.pointers.values());
    if (pts.length < 2) return 0;
    return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
  }

  private applyPan(dxPx: number, dyPx: number): void {
    if (!this.frameW) return;
    const { sw } = computeSourceRect(this.naturalW, this.naturalH, this.aspect, this.t);
    const k = this.frameW / sw;
    // Arrastar a imagem para a direita (dx>0) move o centro-fonte para a esquerda.
    this.t.cx = this.t.cx - dxPx / (k * this.naturalW);
    this.t.cy = this.t.cy - dyPx / (k * this.naturalH);
    this.reclampAndRender(true);
  }

  private applyZoom(scale: number): void {
    this.t.scale = Math.min(CROP_MAX_ZOOM, Math.max(1, scale));
    this.reclampAndRender(true);
  }
}
